import { DefaultError, useMutation } from "@tanstack/react-query";
import { useQueryToast } from "hooks";
import { apiClient } from "utils";
import { useResponseHandler } from "utils/service/responseHandler";
import { IMusicProductPhysicalImageResponse } from "./types/query";
import i18next from "i18next";

export const usePostMusicProductPhysicalImage = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "usePostMusicProductPhysicalImage"
  );
  return useMutation<
    IMusicProductPhysicalImageResponse,
    DefaultError,
    { file: File; id: string }
  >({
    mutationFn: async ({ file, id }) => {
      loading(
        i18next.t("service-message.add.loading", {
          what: i18next.t("music-product-physical.album-cover-what"),
        })
      );
      const formData = new FormData();

      formData.append("file", file);

      const response = await apiClient<FormData>({
        method: "POST",
        url: `MusicProductPhysical/Image`,
        body: formData,
        params: { mpdid: id },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: "usePostMusicProductPhysicalImage",
        notFoundPath: "/music-product-physical",
      });
    },
    onSuccess: () => {
      success(
        i18next.t("service-message.add.success", {
          what: i18next.t("music-product-physical.album-cover-what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutMusicProductPhysicalImage = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "usePutMusicProductPhysicalImage"
  );
  return useMutation<{}, DefaultError, File>({
    mutationFn: async (file) => {
      loading(
        i18next.t("service-message.change.loading", {
          what: i18next.t("music-product-physical.album-cover-what"),
        })
      );

      const formData = new FormData();

      formData.append("file", file);

      const response = await apiClient<FormData>({
        method: "PUT",
        url: `MusicProductPhysical/Image`,
        body: formData,
        params: { mpdid: id },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: "usePutMusicProductPhysicalImage",
        notFoundPath: "/music-product-physical",
      });
    },
    onSuccess: () => {
      success(
        i18next.t("service-message.change.success", {
          what: i18next.t("music-product-physical.album-cover-what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteMusicProductPhysicalImage = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "useDeleteMusicProductPhysicalImage"
  );
  return useMutation<{}, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t("service-message.remove.loading", {
          what: i18next.t("music-product-physical.album-cover-what"),
        })
      );

      const response = await apiClient<FormData>({
        method: "DELETE",
        url: `MusicProductPhysical/Image/${id}`,
      });

      return responseHandler(response, {
        groupName: "useDeleteMusicProductPhysicalImage",
        notFoundPath: "/music-product-physical",
      });
    },
    onSuccess: () => {
      success(
        i18next.t("service-message.remove.success", {
          what: i18next.t("music-product-physical.album-cover-what"),
        })
      );
    },
    onError: dismiss,
  });
};
